import React, { useEffect, useMemo, useState } from "react";
import { ActivityCard, MixedText, ProgressPill, shuffled } from "../shared";

/**
 * PersonSortGame — sort each word into its bucket, one at a time; wrong
 * answers re-queue at the end. Completes when everything is sorted.
 *
 * Buckets and items match on bucket.key / item.bucket, falling back to the
 * lesson-1 person fields (bucket.person / item.person). Registered as both
 * person_sort (lesson 1's speaker/spoken-to/spoken-about) and bucket_sort
 * (lesson 3's مذكر/مؤنث and lesson 27's four broken-plural shapes, which
 * also show each item's en gloss). Buckets lay out two-up when there is an
 * even number of them, three-up otherwise.
 */
const bucketKeyOf = (bucket) => (bucket.key !== undefined ? bucket.key : bucket.person);
const itemKeyOf = (item) => (item.bucket !== undefined ? item.bucket : item.person);

export default function PersonSortGame({ data, onComplete, audio }) {
  const [queue, setQueue] = useState(() => shuffled(data.items));
  const [sortedCount, setSortedCount] = useState(0);
  const [streak, setStreak] = useState(0);
  const [feedback, setFeedback] = useState(null); // { correct, bucketPerson }
  const total = data.items.length;
  const current = queue[0] || null;
  const done = sortedCount >= total;

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  useEffect(() => {
    if (current) audio.play(current.arabic);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current && current.arabic]);

  const buckets = useMemo(() => data.buckets, [data]);

  const handlePick = (bucket) => {
    if (!current || feedback) return;
    const correct = bucketKeyOf(bucket) === itemKeyOf(current);
    setFeedback({ correct, bucketKey: bucketKeyOf(bucket) });
    setTimeout(() => {
      setFeedback(null);
      if (correct) {
        setSortedCount((n) => n + 1);
        setStreak((s) => s + 1);
        setQueue((q) => q.slice(1));
      } else {
        setStreak(0);
        setQueue((q) => [...q.slice(1), q[0]]); // re-queue for another try
      }
    }, 700);
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-between gap-3 mb-5">
        <ProgressPill>{sortedCount}/{total} sorted</ProgressPill>
        {streak >= 3 && (
          <span className="text-sm font-semibold" style={{ color: "var(--gold-bright)" }}>
            🔥 {streak} in a row
          </span>
        )}
      </div>

      {!done && current && (
        <>
          <div className="flex flex-col items-center mb-6">
            <span
              dir="rtl"
              lang="ar"
              className={`font-arabic text-6xl leading-[1.6] px-8 pt-9 pb-3 rounded-2xl transition-all ${feedback && !feedback.correct ? "animate-pulse" : ""}`}
              style={{
                color: feedback ? (feedback.correct ? "var(--teal)" : "#e58c8c") : "var(--parchment)",
                background: "var(--ink)",
                border: "1px solid var(--panel-line)",
              }}
            >
              {current.arabic}
            </span>
            {current.en && (
              <span className="text-sm mt-2" style={{ color: "var(--parchment-dim)" }}>{current.en}</span>
            )}
          </div>

          <div className={`grid gap-2.5 ${buckets.length % 2 === 0 ? "grid-cols-2" : "grid-cols-1 sm:grid-cols-3"}`}>
            {buckets.map((bucket) => {
              const isPicked = feedback && feedback.bucketKey === bucketKeyOf(bucket);
              const border = isPicked
                ? feedback.correct
                  ? "1px solid var(--teal)"
                  : "1px solid #dc5a5a"
                : "1px solid var(--panel-line)";
              return (
                <button
                  key={bucketKeyOf(bucket)}
                  type="button"
                  onClick={() => handlePick(bucket)}
                  disabled={!!feedback}
                  className="rounded-xl px-4 py-4 text-center transition-all hover:brightness-110 disabled:cursor-default"
                  style={{ background: "var(--ink)", border }}
                >
                  <span dir="rtl" lang="ar" className="font-arabic text-2xl block" style={{ color: "var(--gold-bright)" }}>
                    {bucket.arabic}
                  </span>
                  <span className="text-sm block mt-1" style={{ color: "var(--parchment)" }}>{bucket.en}</span>
                  <span className="text-xs block mt-0.5" style={{ color: "var(--parchment-dim)" }}>{bucket.hint}</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {done && (
        <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl" style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}>
          <span className="text-lg leading-none" aria-hidden="true">🎯</span>
          <p className="text-sm m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.done_message || "All twelve sorted — you can tell who's speaking, who's spoken to, and who's spoken about."} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
